import { useCallback, useState } from 'react';
import MojitoCore from 'mojito/core';
import MojitoPresentation from 'mojito/presentation';
import MojitoServices from 'mojito/services';
import EventCardMarketsSlider from 'modules/event-cards/markets-slider/index.js';
import { useEventItem, useEventsLoadDone } from 'modules/common/hooks';
import withEventCardSkeleton from 'modules/event-cards/skeleton/with-event-card-skeleton.js';

const { FlexPane, Text, Button, Timer, ImageButton, SportAndCountryIcon } =
    MojitoPresentation.Components;
const { IntentActionTrigger } = MojitoPresentation.Base.Intent;
const Scoreboard = MojitoPresentation.Components.Scoreboard.default;
const { EventFormatter } = MojitoPresentation.Utils;
const EventsUtils = MojitoServices.SportsContent.Events.utils;
const VideoStreamsUtils = MojitoServices.VideoStreaming.utils;
const uiStyle = MojitoCore.Presentation.uiStyle;

const VerticalMatchEventCard = withEventCardSkeleton(
    props => {
        const {
            mojitoTools,
            eventId,
            marketId,
            shouldRequestData,
            elementRef,
            backgroundImageUrl,
        } = props;
        const { config, style, appContext, stringResolver, l10n } = mojitoTools;

        const [isCardHovered, setIsCardHovered] = useState(false);
        const onCardMouseEnter = useCallback(() => setIsCardHovered(true), []);
        const onCardMouseLeave = useCallback(() => setIsCardHovered(false), []);

        const event = useEventItem(eventId, shouldRequestData);
        const onEventClick = useCallback(() => {
            IntentActionTrigger.showSportEvent(event.sportId, event.id);
            appContext.analyticsEmitter.emitAnalytics('eventCardClicked', event.canonicalName);
        }, [event?.sportId, event?.id, event?.canonicalName, appContext.analyticsEmitter]);

        if (!event) {
            return null;
        }

        const altText =
            appContext.sportName(event.sportId) || stringResolver.resolveString('$GENERIC.DEFAULT');
        const isInPlay = event.details.inPlay;
        const showStreamButton = isInPlay && VideoStreamsUtils.isStreamAvailable(event);
        const cardWrapperStyle = isInPlay ? style.liveCardWrapper : style.cardWrapper;
        const sportBackgroundColor =
            config.cardSurface?.sportBackgroundColors?.[event.sportId] ||
            config.cardSurface?.defaultBackgroundColor;
        const cardSurfaceColor =
            sportBackgroundColor && config.cardSurface?.backgroundColorSoftener
                ? `color-mix(in srgb, ${sportBackgroundColor} ${
                      config.cardSurface.sportBackgroundColorWeight || '35%'
                  }, ${config.cardSurface.backgroundColorSoftener})`
                : sportBackgroundColor;
        const cardSurfaceStyle = uiStyle.merge(style.cardSurface, {
            ...(cardSurfaceColor && { backgroundColor: cardSurfaceColor }),
            ...(config.cardSurface?.gradientOverlay && {
                backgroundImage: config.cardSurface.gradientOverlay,
            }),
        });
        const fallbackImage =
            config.photoLayer?.backgroundImages?.[event.sportId] ||
            config.photoLayer?.backgroundImages?.DEFAULT;
        const imageUrl =
            backgroundImageUrl || (fallbackImage && appContext.getImage(fallbackImage));
        const photoLayerStyle = uiStyle.merge(style.photoLayer, {
            ...(imageUrl && { backgroundImage: `url(${imageUrl})` }),
            ...(config.photoLayer?.borderColorFromCardSurface &&
                cardSurfaceColor && {
                    borderColor: `color-mix(in srgb, ${cardSurfaceColor} ${
                        config.photoLayer.borderColorSportWeight || '70%'
                    }, black)`,
                }),
        });

        return (
            <div
                className="ta-VerticalMatchEventCard"
                style={cardWrapperStyle}
                ref={elementRef}
                onMouseEnter={onCardMouseEnter}
                onMouseLeave={onCardMouseLeave}
            >
                {config.cardSurface?.enabled !== false && (
                    <div className="ta-cardSurface" style={cardSurfaceStyle} />
                )}
                {config.photoLayer?.enabled !== false && imageUrl && (
                    <div className="ta-photoLayer" style={photoLayerStyle} />
                )}
                {config.backdropOverlays?.layer1?.enabled && (
                    <div className="ta-backdropLayer1" style={style.backdropLayer1} />
                )}
                {config.backdropOverlays?.layer2?.enabled && (
                    <div className="ta-backdropLayer2" style={style.backdropLayer2} />
                )}
                <FlexPane class="ta-eventCardContainer" config={config.eventCard.container}>
                    <Button
                        class="ta-clickableArea"
                        onClick={onEventClick}
                        config={config.eventCard.clickableArea}
                    >
                        <FlexPane class="ta-headerContainer" config={config.headerContainer}>
                            <FlexPane class="ta-headerLeft" config={config.headerLeft}>
                                <SportAndCountryIcon
                                    sportId={event.sportId}
                                    countryCode={event.countryCode}
                                    countryName={event.countryName}
                                    alt={altText}
                                    config={config.sportCountryIcons}
                                />
                                <Text class="ta-headerTitle" config={config.eventCard.title}>
                                    {event.typeName}
                                </Text>
                                {isInPlay && (
                                    <LiveIndicator
                                        event={event}
                                        config={config.liveIndicator}
                                        l10n={l10n}
                                    />
                                )}
                            </FlexPane>
                            {showStreamButton && (
                                <ImageButton
                                    class="ta-streamButton"
                                    onClick={onEventClick}
                                    config={config.streamButton}
                                />
                            )}
                        </FlexPane>
                    </Button>
                    <FlexPane config={config.participantsAndMarketsContainer}>
                        <Button
                            class="ta-clickableArea"
                            onClick={onEventClick}
                            config={config.eventCard.clickableArea}
                        >
                            <Scoreboard
                                event={event}
                                config={config.scoreboard}
                                isOutright={EventsUtils.isOutrightEventType(event.eventType)}
                                inplayTime={EventsUtils.getEventInplayTime(event)}
                                hasClockDetails={EventsUtils.hasInPlayClockDetails(event)}
                            />
                        </Button>
                        <EventCardMarketsSlider
                            config={config.marketsSlider}
                            event={event}
                            marketId={marketId}
                            shouldRequestData={shouldRequestData}
                            isCardHovered={isCardHovered}
                        />
                    </FlexPane>
                </FlexPane>
            </div>
        );
    },
    props => useEventsLoadDone([props.eventId]),
    config => config.eventCard.skeletonLoader
);

const LiveIndicator = ({ event, config, l10n }) => {
    const inplayTime = EventsUtils.getEventInplayTime(event);
    const hasClockDetails = EventsUtils.hasInPlayClockDetails(event);
    // Set-based sports show the current set instead of a clock.
    const isSetBased = config.setBasedSports?.includes(event.sportId);
    const setDescription = isSetBased
        ? EventFormatter.getDescription(event.sportId, event.details.inplayDescription, l10n)
        : '';
    const showTime = !isSetBased && (hasClockDetails || inplayTime > 0);

    return (
        <FlexPane class="ta-LiveIndicator" config={config.container}>
            <FlexPane config={config.dot} />
            {isSetBased && setDescription && <Text config={config.time}>{setDescription}</Text>}
            {showTime && (
                <Text config={config.time}>
                    {hasClockDetails ? (
                        <Timer
                            config={config.timer}
                            time={inplayTime}
                            direction={event.details.clockDirection}
                            running={event.details.isClockRunning}
                        />
                    ) : (
                        `${Math.max(0, Math.floor(inplayTime / 60))}'`
                    )}
                </Text>
            )}
        </FlexPane>
    );
};

VerticalMatchEventCard.getStyle = (config, mode, merge) => {
    const backdropBaseStyle = {
        position: 'absolute',
        pointerEvents: 'none',
    };
    const cardWrapper = merge(
        {
            position: 'relative',
            overflow: 'hidden',
            width: '100%',
            isolation: 'isolate',
        },
        config.cardWrapper || {}
    );

    return {
        cardWrapper,
        liveCardWrapper: merge(cardWrapper, config.liveCardWrapper || {}),
        cardSurface: merge(
            backdropBaseStyle,
            { top: 0, right: 0, bottom: 0, left: 0 },
            config.cardSurface?.style || {}
        ),
        photoLayer: merge(
            backdropBaseStyle,
            {
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
            },
            config.photoLayer?.style || {}
        ),
        backdropLayer1: merge(backdropBaseStyle, config.backdropOverlays?.layer1?.style || {}),
        backdropLayer2: merge(backdropBaseStyle, config.backdropOverlays?.layer2?.style || {}),
    };
};

export default VerticalMatchEventCard;
